import { Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/lib/i18n";
import Index from "@/pages/Index";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Support from "@/pages/Support";
import Blog from "@/pages/Blog";
import BlogScan from "@/pages/BlogScan";
import BlogList from "@/pages/BlogList";
import Invite from "@/pages/Invite";
import ParentsLanding from "@/pages/ParentsLanding";
import RenoLanding from "@/pages/RenoLanding";
import DownloadRedirect from "@/pages/DownloadRedirect";
import DownloadPage from "@/pages/DownloadPage";
import Demo from "@/pages/Demo";
import DeleteAccount from "@/pages/DeleteAccount";
import NotFound from "@/pages/NotFound";
import { ContentPage } from "@/components/content/ContentPage";
import { ArticlePage } from "@/components/content/ArticlePage";
import { getPillar } from "@/data/pillars";
import { getFeature } from "@/data/features";
import { getPost } from "@/data/posts";
import { getMerchantPage } from "@/data/merchants";
import { jsonPosts } from "@/lib/blog/registry";
import JsonBlogArticle from "@/pages/JsonBlogArticle";
import { SeoNavigationCleanup } from "./SeoNavigationCleanup";

const receiptsPillar = getPillar("receipts")!;
const returnsPillar = getPillar("returns")!;
const warrantiesPillar = getPillar("warranties")!;
const familyPillar = getPillar("family")!;

const featureOcr = getFeature("ocr")!;
const featureCloud = getFeature("cloud")!;
const featureFilters = getFeature("filters")!;

const postFaded = getPost("recu-efface-quoi-faire")!;
const postWarranty = getPost("comment-conserver-recus-garanties")!;
const postReturn = getPost("comment-faire-retour-magasin-avec-telephone")!;
const postBest = getPost("meilleure-application-recus-canada")!;
const postShare = getPost("partager-recu-conjoint-famille")!;
const postWarrantyTypes = getPost("garantie-magasin-vs-garantie-manufacturier")!;
const postChoose = getPost("comment-choisir-application-recus")!;

const merchantCanadianTire = getMerchantPage("canadian-tire")!;
const merchantIkea = getMerchantPage("ikea")!;
const merchantHomeDepot = getMerchantPage("home-depot")!;
const merchantRona = getMerchantPage("rona")!;

/**
 * All app routes, wrapped in the LanguageProvider (which reads the URL via
 * useLocation, so it must live inside a Router). Router-agnostic: works under
 * BrowserRouter (client) and StaticRouter (server prerender).
 */
export function AppRoutes() {
  return (
    <LanguageProvider>
      <SeoNavigationCleanup />
      <Routes>
        {/* French routes (default) */}
        <Route path="/" element={<Index />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        {/* English routes */}
        <Route path="/en" element={<Index />} />
        <Route path="/en/privacy" element={<Privacy />} />
        <Route path="/en/terms" element={<Terms />} />
        <Route path="/en/support" element={<Support />} />
        {/* SEO pillar content pages */}
        <Route path="/application-recus" element={<ContentPage page={receiptsPillar} />} />
        <Route path="/en/receipt-app" element={<ContentPage page={receiptsPillar} />} />
        <Route path="/application-retours-magasin" element={<ContentPage page={returnsPillar} />} />
        <Route path="/en/returns-app" element={<ContentPage page={returnsPillar} />} />
        <Route path="/application-garanties" element={<ContentPage page={warrantiesPillar} />} />
        <Route path="/en/warranty-tracker-app" element={<ContentPage page={warrantiesPillar} />} />
        <Route path="/application-recus-famille" element={<ContentPage page={familyPillar} />} />
        <Route path="/en/family-receipt-app" element={<ContentPage page={familyPillar} />} />
        {/* Feature pages (pillar-style) */}
        <Route path="/fonctionnalites/precision-ocr" element={<ContentPage page={featureOcr} />} />
        <Route path="/en/features/ocr-accuracy" element={<ContentPage page={featureOcr} />} />
        <Route path="/fonctionnalites/stockage-cloud-securise" element={<ContentPage page={featureCloud} />} />
        <Route path="/en/features/secure-cloud-storage" element={<ContentPage page={featureCloud} />} />
        <Route path="/fonctionnalites/categorisation-filtres" element={<ContentPage page={featureFilters} />} />
        <Route path="/en/features/categories-filters" element={<ContentPage page={featureFilters} />} />
        {/* Merchant receipt pages (examples from a ReFound demo account) */}
        <Route path="/marchands/recus-canadian-tire" element={<ContentPage page={merchantCanadianTire} />} />
        <Route path="/en/merchants/canadian-tire-receipts" element={<ContentPage page={merchantCanadianTire} />} />
        <Route path="/marchands/recus-ikea" element={<ContentPage page={merchantIkea} />} />
        <Route path="/en/merchants/ikea-receipts" element={<ContentPage page={merchantIkea} />} />
        <Route path="/marchands/recus-home-depot" element={<ContentPage page={merchantHomeDepot} />} />
        <Route path="/en/merchants/home-depot-receipts" element={<ContentPage page={merchantHomeDepot} />} />
        <Route path="/marchands/recus-rona" element={<ContentPage page={merchantRona} />} />
        <Route path="/en/merchants/rona-receipts" element={<ContentPage page={merchantRona} />} />
        {/* Blog listing */}
        <Route path="/blog" element={<BlogList />} />
        <Route path="/en/blog" element={<BlogList />} />
        {/* Individual articles */}
        {/* Article 1 */}
        <Route path="/blog/meilleure-app-recu-2026" element={<Blog />} />
        <Route path="/en/blog/meilleure-app-recu-2026" element={<Blog />} />
        {/* Article 2 */}
        <Route path="/blog/comment-numeriser-recus-application-mobile" element={<BlogScan />} />
        <Route path="/en/blog/comment-numeriser-recus-application-mobile" element={<BlogScan />} />
        {/* Article: reçu effacé */}
        <Route path="/blog/recu-efface-quoi-faire" element={<ArticlePage post={postFaded} />} />
        <Route path="/en/blog/faded-receipt-what-to-do" element={<ArticlePage post={postFaded} />} />
        {/* Article: conserver reçus garanties */}
        <Route path="/blog/comment-conserver-recus-garanties" element={<ArticlePage post={postWarranty} />} />
        <Route path="/en/blog/how-to-keep-receipts-for-warranties" element={<ArticlePage post={postWarranty} />} />
        {/* Article: garantie magasin vs manufacturier */}
        <Route path="/blog/garantie-magasin-vs-garantie-manufacturier" element={<ArticlePage post={postWarrantyTypes} />} />
        <Route path="/en/blog/store-warranty-vs-manufacturer-warranty" element={<ArticlePage post={postWarrantyTypes} />} />
        {/* Article: retour en magasin avec téléphone */}
        <Route path="/blog/comment-faire-retour-magasin-avec-telephone" element={<ArticlePage post={postReturn} />} />
        <Route path="/en/blog/how-to-return-items-in-store-with-your-phone" element={<ArticlePage post={postReturn} />} />
        {/* Article: meilleure application de reçus au Canada */}
        <Route path="/blog/meilleure-application-recus-canada" element={<ArticlePage post={postBest} />} />
        <Route path="/en/blog/best-receipt-app-canada" element={<ArticlePage post={postBest} />} />
        {/* Article: partager un reçu avec son conjoint */}
        <Route path="/blog/partager-recu-conjoint-famille" element={<ArticlePage post={postShare} />} />
        <Route path="/en/blog/share-receipt-with-partner-family" element={<ArticlePage post={postShare} />} />
        {/* Article: comment choisir une application de reçus */}
        <Route path="/blog/comment-choisir-application-recus" element={<ArticlePage post={postChoose} />} />
        <Route path="/en/blog/how-to-choose-receipt-app" element={<ArticlePage post={postChoose} />} />
        {/* Meta-ads landing page (parents / families) */}
        <Route path="/parents-recus-retours" element={<ParentsLanding />} />
        <Route path="/en/parents-receipts-returns" element={<ParentsLanding />} />
        {/* Meta-ads landing page (home reno / DIY) */}
        <Route path="/reno-recus-retours" element={<RenoLanding />} />
        <Route path="/en/home-reno-receipts-returns" element={<RenoLanding />} />
        {/* Demo video page — direct link for press and Apple, not in the nav */}
        <Route path="/demo" element={<Demo />} />
        <Route path="/en/demo" element={<Demo />} />
        {/* Download waypoint with immediate, same-tab manual store links */}
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/en/download" element={<DownloadPage />} />
        {/* Short links: always one store (refoundapp.ca/android, refoundapp.ca/ios) */}
        <Route path="/android" element={<DownloadRedirect forceStore="play" />} />
        <Route path="/en/android" element={<DownloadRedirect forceStore="play" />} />
        <Route path="/ios" element={<DownloadRedirect forceStore="ios" />} />
        <Route path="/en/ios" element={<DownloadRedirect forceStore="ios" />} />
        {/* Suppression de compte (exigence Google Play) */}
        <Route path="/supprimer-compte" element={<DeleteAccount />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/en/delete-account" element={<DeleteAccount />} />
        {/* Page de repli partage de recu (Universal Links / App Links) */}
        <Route path="/invite/:token" element={<Invite />} />
        {/* JSON articles register automatically in both languages. */}
        {jsonPosts.flatMap((post) => [
          <Route key={post.frPath} path={post.frPath} element={<JsonBlogArticle post={post} />} />,
          <Route key={post.enPath} path={post.enPath} element={<JsonBlogArticle post={post} />} />,
        ])}
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </LanguageProvider>
  );
}
